import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Drawer,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { ClearOutlined, ReloadOutlined, SearchOutlined, SwapOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get, patch, post } from '../../api/client';

const { Text } = Typography;

interface MergeSecondarySnapshot {
  id: string;
  fullName: string | null;
  hadAccount: boolean;
}

interface ReassignEntry {
  orderId: string;
  orderNumber: string;
  fromPartyId: string;
  toPartyId: string;
  note: string;
  at: string;
}

interface MergeLogRow {
  id: string;
  primaryId: string;
  primaryName: string | null;
  triggerSource: string;
  secondary: MergeSecondarySnapshot[];
  orderIds: string[];
  ordersMoved: number;
  staffMoved: number;
  tagsMoved: number;
  disputed: boolean;
  disputeNote: string | null;
  disputedAt: string | null;
  reassignments: ReassignEntry[];
  createdAt: string;
}

interface MergeArchiveState {
  id: string;
  fullName: string | null;
  status: string | null;
  isUser: boolean;
  role: 'primary' | 'secondary';
  exists: boolean;
}

interface MergeOrderState {
  id: string;
  orderNumber: string;
  customerId: string;
  orderAmount: number;
  paidAmount: number;
  orderStatus: string;
  approvalStatus: string;
  orderStartDate: string;
  isDeleted: boolean;
  movable: boolean;
  reassignedTimes: number;
}

interface ListResult {
  records: MergeLogRow[];
  total: number;
  page: number;
  pageSize: number;
}

interface DetailResult {
  log: MergeLogRow;
  archives: MergeArchiveState[];
  orders: MergeOrderState[];
}

const TRIGGER_LABEL: Record<string, string> = {
  register: '注册时自动收敛',
  merge_self_proved: '本人自证合并',
  merge_sms_verified: '短信验证后合并',
};

const stamp = (value: string | null | undefined) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—');
const money = (value: number) => `¥${Number(value || 0).toFixed(2)}`;

/**
 * 档案姓名的显示口径：留痕存的是合并当时的快照，档案后来被整行抹除时也照样要说得出是谁。
 * exists=false 只影响「现在还能不能把订单调给它」，不影响这里怎么称呼它。
 */
const nameOf = (archives: MergeArchiveState[], id: string): string => {
  const found = archives.find((a) => a.id === id);
  if (!found) return '（不在这批档案里）';
  return found.fullName || (found.exists ? '（无姓名）' : '（档案已抹除）');
};

export default function IdentityMergerPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [searched, setSearched] = useState('');
  const [disputed, setDisputed] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [detailId, setDetailId] = useState<string | null>(null);
  const [disputeTarget, setDisputeTarget] = useState<MergeLogRow | null>(null);
  const [disputeNote, setDisputeNote] = useState('');
  const [reassignOrder, setReassignOrder] = useState<MergeOrderState | null>(null);
  const [reassignTo, setReassignTo] = useState<string | undefined>(undefined);
  const [reassignNote, setReassignNote] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['identity-merges', searched, disputed, page],
    queryFn: () =>
      get<ListResult>('/admin/identity-merges', {
        keyword: searched || undefined,
        disputed,
        page,
        pageSize,
      }),
  });

  const { data: detail, isFetching: detailLoading } = useQuery({
    queryKey: ['identity-merges', detailId],
    queryFn: () => get<DetailResult>(`/admin/identity-merges/${detailId}`),
    enabled: detailId !== null,
  });

  const rows = detail?.log ?? disputeTarget ?? null;

  const dispute = useMutation({
    mutationFn: ({ id, disputed: flag, note }: { id: string; disputed: boolean; note: string }) =>
      patch<MergeLogRow>(`/admin/identity-merges/${id}/dispute`, { disputed: flag, note }),
    onSuccess: (log) => {
      message.success(log.disputed ? '已标记该合并存在争议，现在可以逐单调整归属' : '已撤销争议标记，改派通道重新关闭');
      setDisputeTarget(null);
      setDisputeNote('');
      void queryClient.invalidateQueries({ queryKey: ['identity-merges'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '标记争议失败'),
  });

  const reassign = useMutation({
    mutationFn: ({ id, orderId, toPartyId, note }: { id: string; orderId: string; toPartyId: string; note: string }) =>
      post<MergeLogRow>(`/admin/identity-merges/${id}/reassign`, { orderId, toPartyId, note }),
    onSuccess: (log) => {
      message.success(`订单归属已调整，本次合并累计改派 ${log.reassignments.length} 笔`);
      setReassignOrder(null);
      setReassignTo(undefined);
      setReassignNote('');
      void queryClient.invalidateQueries({ queryKey: ['identity-merges'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '改派失败'),
  });

  const columns: TableProps<MergeLogRow>['columns'] = [
    { title: '合并时间', dataIndex: 'createdAt', width: 150, render: stamp },
    {
      title: '主档',
      key: 'primary',
      width: 160,
      render: (_, r) => <Text strong>{r.primaryName || '（无姓名）'}</Text>,
    },
    {
      title: '并入的次档',
      key: 'secondary',
      width: 240,
      render: (_, r) =>
        r.secondary.length === 0 ? (
          <Text type="secondary">—</Text>
        ) : (
          <Space size={4} wrap>
            {r.secondary.map((s) => (
              <Tooltip key={s.id} title={s.hadAccount ? '该次档原本承载注册账号' : '仅作为客户档案并入'}>
                <Tag>{s.fullName || '（无姓名）'}</Tag>
              </Tooltip>
            ))}
          </Space>
        ),
    },
    {
      title: '触发来源',
      dataIndex: 'triggerSource',
      width: 150,
      render: (v: string) => TRIGGER_LABEL[v] ?? v,
    },
    {
      title: '迁走订单',
      key: 'orders',
      width: 110,
      render: (_, r) => `${r.orderIds.length} 单`,
    },
    {
      title: '人员/标签',
      key: 'moved',
      width: 110,
      render: (_, r) => `${r.staffMoved} / ${r.tagsMoved}`,
    },
    {
      title: '争议',
      key: 'disputed',
      width: 130,
      render: (_, r) =>
        r.disputed ? (
          <Tooltip title={r.disputeNote ?? undefined}>
            <Tag color="red">有争议</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">无争议</Tag>
        ),
    },
    {
      title: '改派',
      key: 'reassign',
      width: 100,
      render: (_, r) => (r.reassignments.length ? <Text>{r.reassignments.length} 笔</Text> : <Text type="secondary">—</Text>),
    },
    {
      title: '操作',
      key: 'action',
      width: 90,
      fixed: 'right',
      render: (_, r) => (
        <Button size="small" onClick={() => setDetailId(r.id)}>
          详情
        </Button>
      ),
    },
  ];

  const orderColumns: TableProps<MergeOrderState>['columns'] = [
    { title: '订单号', dataIndex: 'orderNumber', width: 170 },
    { title: '当前归属', key: 'customer', width: 140, render: (_, o) => nameOf(detail?.archives ?? [], o.customerId) },
    { title: '金额', dataIndex: 'orderAmount', width: 110, render: money },
    { title: '已收', dataIndex: 'paidAmount', width: 110, render: money },
    { title: '订单状态', key: 'status', width: 150, render: (_, o) => `${o.orderStatus} / ${o.approvalStatus}` },
    {
      title: '可调整',
      key: 'movable',
      width: 200,
      fixed: 'right',
      render: (_, o) =>
        o.isDeleted ? (
          <Text type="danger">订单已删除</Text>
        ) : o.movable ? (
          <Space size={4}>
            <Button
              size="small"
              icon={<SwapOutlined />}
              disabled={!detail?.log.disputed}
              onClick={() => {
                setReassignOrder(o);
                setReassignTo(undefined);
                setReassignNote('');
              }}
            >
              调整归属
            </Button>
            {o.reassignedTimes > 0 && <Text type="secondary">已调整 {o.reassignedTimes} 次</Text>}
          </Space>
        ) : (
          <Text type="secondary">已不在这批档案名下</Text>
        ),
    },
  ];

  const open = detailId !== null;

  return (
    <Card
      title="身份合并台账"
      extra={
        <Space wrap>
          <Input
            allowClear
            placeholder="按主档姓名搜索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onPressEnter={() => {
              setSearched(keyword.trim());
              setPage(1);
            }}
            style={{ width: 180 }}
          />
          <Select
            allowClear
            placeholder="争议状态"
            value={disputed}
            style={{ width: 130 }}
            onChange={(v) => {
              setDisputed(v);
              setPage(1);
            }}
            options={[
              { value: 'true', label: '仅有争议' },
              { value: 'false', label: '仅无争议' },
            ]}
          />
          <Button
            icon={<SearchOutlined />}
            onClick={() => {
              setSearched(keyword.trim());
              setPage(1);
            }}
          >
            查询
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => queryClient.invalidateQueries({ queryKey: ['identity-merges'] })} />
        </Space>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="并错的档案怎么拆回来"
        description="档案一旦并入，订单的客户归属按设计不再可改。这里提供唯一的例外：先在台账上标记争议并写明判定理由，再把这次合并迁走的订单退回到这批被合并档案中的某一个。系统只登记事实，不做自动判定；改派每一笔都留痕，可反复核对也可反向撤回。"
      />
      <Table<MergeLogRow>
        rowKey="id"
        loading={isLoading}
        dataSource={data?.records ?? []}
        columns={columns}
        scroll={{ x: 1250 }}
        locale={{ emptyText: '还没有身份合并记录：注册时命中同一人、或本人在合并向导里确认合并后才会出现在这里' }}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />

      <Drawer
        title="合并详情"
        width={880}
        open={open}
        onClose={() => setDetailId(null)}
        loading={detailLoading && !detail}
        extra={
          rows && (
            <Button
              danger={rows.disputed}
              icon={rows.disputed ? <ClearOutlined /> : undefined}
              onClick={() => {
                setDisputeTarget(rows);
                setDisputeNote(rows.disputeNote ?? '');
              }}
            >
              {rows.disputed ? '撤销争议标记' : '标记为有争议'}
            </Button>
          )
        }
      >
        {detail && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="合并时间">{stamp(detail.log.createdAt)}</Descriptions.Item>
              <Descriptions.Item label="触发来源">{TRIGGER_LABEL[detail.log.triggerSource] ?? detail.log.triggerSource}</Descriptions.Item>
              <Descriptions.Item label="主档">{detail.log.primaryName || '（无姓名）'}</Descriptions.Item>
              <Descriptions.Item label="迁走订单">{detail.log.orderIds.length} 单</Descriptions.Item>
              <Descriptions.Item label="争议标记">
                {detail.log.disputed ? <Tag color="red">有争议</Tag> : <Tag color="green">无争议</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="标记时间">{stamp(detail.log.disputedAt)}</Descriptions.Item>
              <Descriptions.Item label="判定理由" span={2}>
                {detail.log.disputeNote || '—'}
              </Descriptions.Item>
            </Descriptions>

            {!detail.log.disputed && (
              <Alert type="warning" showIcon message="未标记争议的台账不能调整订单归属，请先在右上角标记争议并写明理由。" />
            )}

            <Table<MergeArchiveState>
              rowKey="id"
              size="small"
              pagination={false}
              dataSource={detail.archives}
              columns={[
                { title: '角色', key: 'role', width: 90, render: (_, a) => (a.role === 'primary' ? '主档' : '次档') },
                { title: '姓名', key: 'name', width: 140, render: (_, a) => a.fullName || '（无姓名）' },
                { title: '状态', dataIndex: 'status', width: 110, render: (v: string | null) => v ?? '—' },
                { title: '可登录', key: 'user', width: 90, render: (_, a) => (a.isUser ? '是' : '否') },
                {
                  title: '档案现状',
                  key: 'exists',
                  width: 120,
                  render: (_, a) => (a.exists ? <Tag color="green">在档</Tag> : <Tag>已抹除</Tag>),
                },
              ]}
            />

            <Table<MergeOrderState>
              rowKey="id"
              size="small"
              pagination={false}
              loading={detailLoading && !detail}
              dataSource={detail.orders}
              columns={orderColumns}
              locale={{ emptyText: '这次合并没有迁走过订单' }}
              scroll={{ x: 880 }}
            />

            {detail.log.reassignments.length > 0 && (
              <div>
                <Text strong>改派记录</Text>
                <Table<ReassignEntry>
                  rowKey={(r) => `${r.orderId}-${r.at}`}
                  size="small"
                  style={{ marginTop: 8 }}
                  pagination={false}
                  dataSource={detail.log.reassignments}
                  columns={[
                    { title: '订单号', dataIndex: 'orderNumber', width: 170 },
                    { title: '从', key: 'from', width: 140, render: (_, r) => nameOf(detail.archives, r.fromPartyId) },
                    { title: '到', key: 'to', width: 140, render: (_, r) => nameOf(detail.archives, r.toPartyId) },
                    { title: '理由', dataIndex: 'note' },
                    { title: '时间', dataIndex: 'at', width: 150, render: stamp },
                  ]}
                />
              </div>
            )}
          </Space>
        )}
      </Drawer>

      <Modal
        title={`${disputeTarget?.disputed ? '撤销' : '标记'}争议`}
        open={disputeTarget !== null}
        onCancel={() => setDisputeTarget(null)}
        onOk={() =>
          disputeTarget &&
          dispute.mutate({ id: disputeTarget.id, disputed: !disputeTarget.disputed, note: disputeNote.trim() })
        }
        okText="提交"
        confirmLoading={dispute.isPending}
        okButtonProps={{ disabled: !disputeNote.trim() }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary">
            判定理由是这条通道的前置开关，不是一句备注：标了争议才允许调整订单归属，撤销标记即关闭通道。
            两种动作都必须留字——标错了要能看出当时为什么标，撤销了才知道是谁把闸门放开的。
          </Text>
          <Input.TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder="判定理由（必填）"
            value={disputeNote}
            onChange={(e) => setDisputeNote(e.target.value)}
          />
        </Space>
      </Modal>

      <Modal
        title="调整订单客户归属"
        open={reassignOrder !== null}
        onCancel={() => setReassignOrder(null)}
        onOk={() =>
          detail &&
          reassignOrder &&
          reassignTo &&
          reassign.mutate({
            id: detail.log.id,
            orderId: reassignOrder.id,
            toPartyId: reassignTo,
            note: reassignNote.trim(),
          })
        }
        okText="确认改派"
        confirmLoading={reassign.isPending}
        okButtonProps={{ disabled: !reassignTo || !reassignNote.trim() }}
      >
        {detail && reassignOrder && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message={`订单 ${reassignOrder.orderNumber} 当前归属「${nameOf(detail.archives, reassignOrder.customerId)}」，只能在本次合并涉及的档案之间调整。`}
            />
            <div>
              <Text>调整到</Text>
              <Select
                style={{ width: '100%', marginTop: 4 }}
                placeholder="选择退回哪个档案"
                value={reassignTo}
                onChange={setReassignTo}
                options={detail.archives
                  .filter((a) => a.exists && a.id !== reassignOrder.customerId)
                  .map((a) => ({ value: a.id, label: `${a.fullName || '（无姓名）'} · ${a.role === 'primary' ? '主档' : '次档'}` }))}
              />
            </div>
            <div>
              <Text>改派理由</Text>
              <Input.TextArea
                rows={3}
                maxLength={500}
                showCount
                style={{ marginTop: 4 }}
                placeholder="为什么要动这一单（必填，会写进台账并通知下单人）"
                value={reassignNote}
                onChange={(e) => setReassignNote(e.target.value)}
              />
            </div>
          </Space>
        )}
      </Modal>
    </Card>
  );
}
